import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { observer } from 'mobx-react';
import { FC } from 'react';
import CashbackListColumnTitle from '../../components/CashbackListColumnTitle/CashbackListColumnTitle';
import CashbackListItem from '../../components/CashbackListItem/CashbackListItem';
import { cashbackStore } from '../../store/cashbackStore';
import { localeStore } from 'src/mobx/localesStore';
import ColumnTitle from 'src/view/components/ColumnTitle/ColumnTitle';
import { DndSensors, dndAutoScrollOptionsDefault, dndOverlayAnimationDefault } from '~/utils/dnd';
import { TOOLTIP_POSITION } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import ScrollableList from '~/view/components/ScrollableList/ScrollableList';
import { TCashback } from '~/view/screens/MarketingScreen/screens/Cashback/types/cashback';
import './style.scss';

const renderCashbackItem = (cashback: TCashback | undefined, isActiveLimitReached: boolean) => {
  return <CashbackListItem key={cashback?.id} id={cashback?.id} cashback={cashback} activeLimitReached={isActiveLimitReached} />;
};

const CashbackTable: FC = observer((): JSX.Element => {
  const dndSensors = DndSensors();

  const handleDragStart = ({ active }: DragStartEvent) => {
    cashbackStore.setDraggingCashbackItem(active.data.current?.cashback);
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    const { cashback } = over?.data.current || {};

    if (!cashback || +active.id === cashback?.id) return;

    await cashbackStore.updateCashbackSorting(+active.id, cashback.priority);
  };

  const handleDragCancel = () => cashbackStore.setDraggingCashbackItem(null);

  return (
    <div className="d-flex flex-column flex-grow-1 pt-4">
      <ul className="d-flex flex-row mb-4 pr-2">
        <CashbackListColumnTitle
          className="w-20"
          itemName={localeStore.t('cashback_table_priority_column_label')}
          tooltip={{ message: localeStore.t('cashback_table_priority_column_hint'), position: TOOLTIP_POSITION.PORTAL }}
        />
        <CashbackListColumnTitle className="w-25" itemName={localeStore.t('hint_name')} />
        <CashbackListColumnTitle className="w-15" itemName={localeStore.t('cashback')} />
        <CashbackListColumnTitle className="w-15" itemName={localeStore.t('start_date')} />
        <CashbackListColumnTitle className="w-15" itemName={localeStore.t('expiry_date')} />
        <ColumnTitle title="txt_delivery_status_h" className="cashback-table__switcher w-10" />
      </ul>
      <DndContext
        id="category"
        autoScroll={dndAutoScrollOptionsDefault}
        sensors={dndSensors}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <ScrollableList
          scrollToTop={false}
          fetch={cashbackStore.fetchCashbackData}
          pages={cashbackStore.pagination.pages || []}
          renderItem={(cashback) => renderCashbackItem(cashback, cashbackStore.hasActiveCashbackLimit)}
          last_page={cashbackStore.pagination.last_page}
          list={cashbackStore.pagination.data}
          current_page={cashbackStore.pagination.current_page}
          loading={cashbackStore.pagination.loading}
          refreshing={cashbackStore.pagination.refreshing}
          scrollbarClassName="overflow-y-scroll"
          scrollbarSize="normal"
          scrollbar
          emptyListInFooter={false}
          showEmptyListImage
          emptyListText={localeStore.t('cashback_not_found')}
          autoFetch
        />

        {cashbackStore.draggingCashbackItem ? (
          <DragOverlay dropAnimation={dndOverlayAnimationDefault}>
            <CashbackListItem
              key={cashbackStore.draggingCashbackItem?.id}
              cashback={cashbackStore.draggingCashbackItem}
              activeLimitReached={cashbackStore.hasActiveCashbackLimit}
            />
          </DragOverlay>
        ) : null}
      </DndContext>
    </div>
  );
});

export default CashbackTable;
